import { RESOURCE_SLUG_PATTERN } from "./slug-grammar.js";
import { isRing } from "./rings.js";
import { PRIVATE_CHANNEL_KEY_PATTERN, channelDisplayNameSchema, channelKeySchema } from "./channel-grammar.js";
import { z } from "zod";
//#region src/release-channels.ts
/**
* Release channels of two kinds, and the shares that offer a private channel to a tenant.
*
* The private-release-channels feature (`requirements/architecture/plans/2026-09-24-private-
* release-channels/`) turns `release.channels` from a fixed four-row ring registry into a registry
* of two kinds. A `public` row is one of the four rings (`stable`, `beta`, `alpha`, `edge`); its
* slug and its routing key are the ring name. A `private` row belongs to one developer's namespace
* (`private/<owner-handle>/<channel-name>`); it routes on a derived key `p-<12 hex>` and sits
* outside the ring order (L-1, L-2, L-3). A share offers one private slug, or every channel under a
* namespace (`private/<handle>/*`), to one tenant org (L-4). The tenant accepts or declines on its
* data plane (L-5).
*
* This file is the one home of the channel shapes, the slug and share-pattern grammars, the
* owner-handle rule, the derived routing key, the floor class and the five bounds of R-010.
* Producers: release-engine's channel and share routes (WS-1) and its desired-state builder (WS-2).
* Consumers: the CP console (WS-5), the `zion-app-dev` MCP tools (WS-6), the `zion` CLI (WS-7),
* the operator (WS-4) and core-api (WS-3), which re-validate what crosses to them.
*
* Direction rule. Request schemas are `z.strictObject`, so a key the server would refuse is refused
* before the request leaves the client. Response schemas are `z.object`, so a member the server adds
* later is stripped and one new column cannot break every reader.
*
* Leading slash (R-026). Every door that takes a private slug or a share pattern strips one leading
* `/` before validation, so `/private/dbrewster/*` and `private/dbrewster/*` name one pattern. The
* input schemas below carry that strip, so a door that parses with them stores the canonical form.
* A second leading slash still fails the grammar.
*/
/**
* The two kinds of `release.channels` row. `public` is one of the four rings; `private` is a
* developer-owned channel. The database carries the same vocabulary as a CHECK on
* `release.channels.kind` (`0026_private_channels.sql`).
*/
const channelKindSchema = z.enum(["public", "private"]);
/**
* Most private channels one namespace may hold. The 101st create in one namespace answers 409.
* Derivation (R-010): with {@link CHANNEL_SHARES_PER_ORG_MAX} shares per org and about 300 bytes per
* offered channel, one org's offers stay near 1.5 MB in the worst case.
*/
const PRIVATE_CHANNELS_PER_NAMESPACE_MAX = 100;
/** Most org ids one share request may name (R-010). A 201st id is a 400 at the schema. */
const CHANNEL_SHARE_ORG_IDS_MAX = 200;
/**
* Most unrevoked shares that may cover one org (R-010). A share that would exceed it answers 409.
* It also caps the offers the envelope carries for one org.
*/
const CHANNEL_SHARES_PER_ORG_MAX = 50;
/** Most items any channel or share list route returns, through `toCappedResponse` (R-010). */
const CHANNEL_LIST_CAP = 500;
/**
* Longest private slug or share pattern, in characters, checked before the grammar runs.
* No derivation: it holds a 64-character handle (the RFC 5321 local-part limit) and a
* 55-character channel name. If a developer hits it, raise it; the column is `text`.
*/
const PRIVATE_CHANNEL_SLUG_MAX_LENGTH = 128;
/**
* Longest channel description, in characters. No derivation: it is a guess sized for one
* paragraph. If a developer hits it, raise it; the column is `text`.
*/
const CHANNEL_DESCRIPTION_MAX_LENGTH = 1024;
/**
* Longest note a developer may attach to a share, in characters. No derivation: a guess sized
* for one sentence. If a developer hits it, raise it; the column is `text`.
*/
const CHANNEL_SHARE_NOTE_MAX_LENGTH = 512;
/** One slug segment: the resource-slug grammar without its anchors (two characters minimum). */
const SEGMENT = RESOURCE_SLUG_PATTERN.source.slice(1, -1);
/** The public-slug alternative: a public slug equals its routing key, so it is the key grammar. */
const PUBLIC_SLUG = `[a-z0-9-]{1,32}`;
/**
* A private channel's slug: `private/<owner-handle>/<channel-name>`, three segments, each segment
* the resource-slug grammar (`[a-z0-9][a-z0-9-]*[a-z0-9]`). Stored and compared without a leading
* slash (R-026). A match does not prove the length bound; apply
* {@link PRIVATE_CHANNEL_SLUG_MAX_LENGTH} first.
*/
const PRIVATE_CHANNEL_SLUG_PATTERN = new RegExp(`^private/${SEGMENT}/${SEGMENT}$`);
/**
* Any channel slug: a public slug (a ring name, which is also its key) or a private slug. The
* publish door's `channel`, the pin door's channel argument and the CLI's `--channel` take this
* grammar (R-014). It admits `canary`, which names no channel; the service answers that with 404.
*/
const CHANNEL_SLUG_PATTERN = new RegExp(`^(?:${PUBLIC_SLUG}|private/${SEGMENT}/${SEGMENT})$`);
/**
* A share pattern: one private slug, or `private/<handle>/*`, which covers every channel in that
* namespace now and later (L-4). No other wildcard form exists: `*` stands for the whole third
* segment and nothing else.
*/
const CHANNEL_SHARE_PATTERN = new RegExp(`^private/${SEGMENT}/(?:${SEGMENT}|\\*)$`);
const PRIVATE_SLUG_MESSAGE = "slug must be private/<handle>/<name>, each segment lowercase letters, digits and inner hyphens";
const CHANNEL_SLUG_MESSAGE = "channel must be a ring name or private/<handle>/<name>, lowercase letters, digits and hyphens";
const SHARE_PATTERN_MESSAGE = "pattern must be private/<handle>/<name> or private/<handle>/*, lowercase letters, digits and hyphens";
/**
* Strip one leading `/` from a slug or pattern a person typed (R-026). A value with no leading
* slash returns unchanged; a value with two returns with one, which then fails every grammar here.
* Pure. Doors that do not parse with the input schemas below call this before validating.
*/
function normalizeChannelSlugInput(value) {
	return value.startsWith("/") ? value.slice(1) : value;
}
/**
* A private slug as a door receives it: one leading slash stripped, then the length bound, then
* {@link PRIVATE_CHANNEL_SLUG_PATTERN}. Parses to the canonical stored form.
*/
const privateChannelSlugInputSchema = z.string().transform(normalizeChannelSlugInput).pipe(z.string().max(128).regex(PRIVATE_CHANNEL_SLUG_PATTERN, PRIVATE_SLUG_MESSAGE));
/**
* Any channel slug as a door receives it (publish, pin, CLI `--channel`): one leading slash
* stripped, the length bound, then {@link CHANNEL_SLUG_PATTERN}. Parses to the canonical form.
*/
const channelSlugInputSchema = z.string().transform(normalizeChannelSlugInput).pipe(z.string().max(128).regex(CHANNEL_SLUG_PATTERN, CHANNEL_SLUG_MESSAGE));
/**
* A share pattern as a door receives it: one leading slash stripped, the length bound, then
* {@link CHANNEL_SHARE_PATTERN}. Parses to the canonical stored form.
*/
const channelSharePatternInputSchema = z.string().transform(normalizeChannelSlugInput).pipe(z.string().max(128).regex(CHANNEL_SHARE_PATTERN, SHARE_PATTERN_MESSAGE));
/**
* True iff share `pattern` covers private channel `slug`.
*
* An exact pattern covers the one slug equal to it. A wildcard `private/<handle>/*` covers every
* private slug whose second segment is `<handle>`; `private/dbrewster/*` does not cover
* `private/dbrewster2/x`. Both arguments are checked against their grammars first and anything
* malformed answers false, so a caller can pass stored strings without pre-validating. Pure.
*
* Callers: release-engine's offer projection and share list filter (WS-1, WS-2), and the operator's
* offer validation (WS-4). The owner check is separate: a pattern names a namespace, and the share
* row's `owner_operator_id` decides whose channels in it are covered.
*/
function matchesChannelSharePattern(pattern, slug) {
	if (!CHANNEL_SHARE_PATTERN.test(pattern) || !PRIVATE_CHANNEL_SLUG_PATTERN.test(slug)) return false;
	if (pattern.endsWith("/*")) return slug.startsWith(pattern.slice(0, -1));
	return pattern === slug;
}
/** The handle segment's own bound: the RFC 5321 local-part limit. */
const OWNER_HANDLE_MAX_LENGTH = 64;
/**
* The owner handle an operator's email yields: the slug of its local part (R-011).
*
* The local part is everything before the last `@`. It is lower-cased, every run of characters
* outside `[a-z0-9]` becomes one `-`, and leading and trailing hyphens are dropped. So
* `dbrewster@witness.ai` gives `dbrewster` and `Dave.Brewster+ci@witness.ai` gives
* `dave-brewster-ci`.
*
* @returns the handle, or `null` when the email has no `@`, when the result is shorter than two
*   characters or longer than 64, or when it fails `RESOURCE_SLUG_PATTERN`. A null handle owns no
*   namespace: such an operator can create a private channel only under `manage-all`, in a
*   namespace another operator already claimed. Pure.
*/
function ownerHandleFromEmail(email) {
	const at = email.lastIndexOf("@");
	if (at <= 0) return null;
	const handle = email.slice(0, at).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
	if (handle.length > OWNER_HANDLE_MAX_LENGTH || !RESOURCE_SLUG_PATTERN.test(handle)) return null;
	return handle;
}
const UUID_PATTERN = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
/**
* The routing key of the private channel whose `release.channels.id` is `channelId`: `p-` and the
* first twelve hex digits of the uuid, hyphens removed, lower-cased (L-2). The result matches
* `PRIVATE_CHANNEL_KEY_PATTERN` and therefore `CHANNEL_KEY_PATTERN`.
*
* Release-engine stores the result as the row's `name` at create time and never changes it. The
* operator recomputes it to reject an offer whose key was not derived from its channel id (WS-4).
*
* @throws Error `privateChannelKeyFor: channel id is not a uuid` when `channelId` is not in the
*   8-4-4-4-12 hex form. Pure.
*/
function privateChannelKeyFor(channelId) {
	if (!UUID_PATTERN.test(channelId)) throw new Error("privateChannelKeyFor: channel id is not a uuid");
	return `p-${channelId.replace(/-/g, "").slice(0, 12).toLowerCase()}`;
}
/**
* One release channel as the CP returns it, of either kind.
*
* - `id`: the `release.channels` uuid.
* - `slug`: the human key. A public row's slug is its ring name; a private row's is
*   `private/<handle>/<name>`.
* - `key`: the routing key, the `release.channels.name` column. The JWT `channel` claim, the
*   `x-zion-channel` header and `activeVersions[].channels` carry it. A ring name, or `p-<12 hex>`.
* - `displayName`: the editable label; the read falls back to the key when the column is null.
* - `kind`: `public` or `private`.
* - `ordinal`: 0 to 3 on a public row (`stable` 0 through `edge` 3); null on a private row.
* - `ownerHandle`, `ownerEmail`: the owning operator's handle and email on a private row; null on a
*   public row.
* - `description`: free text or null.
*
* The schema refuses a row that breaks the kind invariant the database enforces with a CHECK: a
* public row has an ordinal, no owner and `slug === key`; a private row has no ordinal, an owner,
* a private slug and a private key.
*/
const releaseChannelSchema = z.object({
	id: z.uuid(),
	slug: z.string().min(1).max(128),
	key: channelKeySchema,
	displayName: channelDisplayNameSchema,
	kind: channelKindSchema,
	ordinal: z.number().int().nonnegative().nullable(),
	ownerHandle: z.string().nullable(),
	ownerEmail: z.string().nullable(),
	description: z.string().nullable()
}).superRefine((row, ctx) => {
	if (row.kind === "public") {
		if (row.ordinal === null) ctx.addIssue({
			code: "custom",
			path: ["ordinal"],
			message: "a public channel has an ordinal"
		});
		if (row.ownerHandle !== null || row.ownerEmail !== null) ctx.addIssue({
			code: "custom",
			path: ["ownerHandle"],
			message: "a public channel has no owner"
		});
		if (row.slug !== row.key) ctx.addIssue({
			code: "custom",
			path: ["slug"],
			message: "a public channel slug equals its key"
		});
		return;
	}
	if (row.ordinal !== null) ctx.addIssue({
		code: "custom",
		path: ["ordinal"],
		message: "a private channel has no ordinal"
	});
	if (row.ownerHandle === null || row.ownerEmail === null) ctx.addIssue({
		code: "custom",
		path: ["ownerHandle"],
		message: "a private channel has an owner"
	});
	if (!PRIVATE_CHANNEL_SLUG_PATTERN.test(row.slug)) ctx.addIssue({
		code: "custom",
		path: ["slug"],
		message: PRIVATE_SLUG_MESSAGE
	});
	if (!PRIVATE_CHANNEL_KEY_PATTERN.test(row.key)) ctx.addIssue({
		code: "custom",
		path: ["key"],
		message: "a private channel key is p- and 12 hex digits"
	});
});
/**
* The ring a private channel counts as for the tenant minimum-ring floor and for sorting (L-7,
* defaults row 11). A developer build is at least as adventurous as `edge`, so a tenant whose floor
* is below `edge` cannot bind a private channel until a platform operator lifts the floor. core-api
* writes it as the private Customer Channel's `ring_name`. Nothing else names the literal.
*/
const PRIVATE_CHANNEL_FLOOR_CLASS = "edge";
/**
* The ring `channel` counts as against a minimum-ring floor: its own key for a public channel,
* {@link PRIVATE_CHANNEL_FLOOR_CLASS} for a private one.
*
* @throws Error `channelFloorRing: public channel key is not a ring` when a public channel's key is
*   not one of the four rings, which the seeded table never holds. Pure.
*/
function channelFloorRing(channel) {
	if (channel.kind === "private") return PRIVATE_CHANNEL_FLOOR_CLASS;
	if (!isRing(channel.key)) throw new Error("channelFloorRing: public channel key is not a ring");
	return channel.key;
}
/** Channel description text: bounded, and free of the NUL character Postgres `text` cannot store. */
const NUL = String.fromCharCode(0);
const channelDescriptionSchema = z.string().max(CHANNEL_DESCRIPTION_MAX_LENGTH).refine((v) => !v.includes(NUL), { message: "description must not contain a NUL character" });
/**
* `POST /v1/channels/private` body. `slug` is a private slug (one leading slash stripped, R-026);
* its handle segment must be the caller's own handle unless the caller holds
* `cp:channel:private:manage-all`, which the service checks. `displayName` is the display-name
* grammar. `description` is optional. No `key`, `kind`, `ordinal` or owner: the service derives
* each.
*/
const createPrivateChannelRequestSchema = z.strictObject({
	slug: privateChannelSlugInputSchema,
	displayName: channelDisplayNameSchema,
	description: channelDescriptionSchema.optional()
});
/**
* `PATCH /v1/channels/:id` (public rows, `cp:channel:update`) and `PATCH /v1/channels/private/:id`
* (private rows, owner or manage-all) body (R-005). Both members are optional; an absent member
* leaves the column alone and `description: null` clears it. `name`, `slug`, `ordinal` and `kind`
* are absent, so the strict object refuses them with 400 (L-10).
*/
const updateReleaseChannelRequestSchema = z.strictObject({
	displayName: channelDisplayNameSchema.optional(),
	description: channelDescriptionSchema.nullable().optional()
});
/**
* `POST /v1/channels/shares` body. `pattern` is a share pattern (one leading slash stripped,
* R-026). `orgIds` names 1 to {@link CHANNEL_SHARE_ORG_IDS_MAX} CP org ids; the service writes one
* share row per distinct id. `note` is optional free text.
*/
const createChannelShareRequestSchema = z.strictObject({
	pattern: channelSharePatternInputSchema,
	orgIds: z.array(z.uuid()).min(1).max(200),
	note: z.string().max(512).refine((v) => !v.includes(NUL), { message: "note must not contain a NUL character" }).optional()
});
/**
* A tenant's decision on a share, as the developer reads it: `pending` until the tenant decides,
* then `accepted` or `declined`. The DP decision tables and the status-report wire carry only the
* two decided states; `pending` is the absence of a decision row.
*/
const channelShareDecisionStateSchema = z.enum([
	"pending",
	"accepted",
	"declined"
]);
/**
* One share as release-engine returns it (R-002): one pattern offered to one org.
*
* - Identity: `id`, `pattern`, `ownerHandle` (the pattern's second segment), `orgId` (CP id),
*   `orgSlug`, `orgName`.
* - Lifecycle: `createdAt`, and `revokedAt`, null until revoked.
* - Tenant state: `state` and `decidedAt`. `state` is `revoked` exactly when `revokedAt` is set;
*   otherwise it is the CP decision projection's state, or `pending` with a null `decidedAt` when
*   the tenant has not decided. A revoked share keeps any `decidedAt` it had.
*
* The tenant's email never appears: the wire decision carries the deciding user's id, and the
* email stays on the data plane.
*/
const channelShareSchema = z.object({
	id: z.uuid(),
	pattern: z.string().regex(CHANNEL_SHARE_PATTERN),
	ownerHandle: z.string().regex(RESOURCE_SLUG_PATTERN),
	orgId: z.uuid(),
	orgSlug: z.string(),
	orgName: z.string(),
	createdAt: z.iso.datetime(),
	revokedAt: z.iso.datetime().nullable(),
	state: z.enum([
		"pending",
		"accepted",
		"declined",
		"revoked"
	]),
	decidedAt: z.iso.datetime().nullable()
}).superRefine((share, ctx) => {
	if (share.state === "revoked" !== (share.revokedAt !== null)) ctx.addIssue({
		code: "custom",
		path: ["state"],
		message: "state is revoked exactly when revokedAt is set"
	});
	if (share.state === "pending" && share.decidedAt !== null) ctx.addIssue({
		code: "custom",
		path: ["decidedAt"],
		message: "a pending share has no decidedAt"
	});
	if ((share.state === "accepted" || share.state === "declined") && share.decidedAt === null) ctx.addIssue({
		code: "custom",
		path: ["decidedAt"],
		message: "a decided share has a decidedAt"
	});
});
/**
* One org a developer may share with, as `GET /v1/channels/share-targets` returns it (R-015): the
* CP org id, slug and name, and nothing else, so sharing needs no `cp:tenant:read`.
*/
const shareTargetSchema = z.object({
	id: z.uuid(),
	slug: z.string(),
	name: z.string()
});
/**
* Every release-engine channel route, in NestJS `:param` form. The CP console, the MCP tools and
* the CLI build URLs from these; release-engine's controllers declare the same paths. Permissions
* are overview §8's: `list` under `cp:platform:read` (public rows only, R-012); `listPrivate`,
* `getPrivate` and `listShares` under `cp:channel:private:read`; `createPrivate` under `:create`;
* `update` (public rows) under `cp:channel:update`; `updatePrivate` under `:update` (R-005);
* `deletePrivate` under `:delete`; `createShares` and `revokeShare` under `:share`; `shareTargets`
* under `:share` (R-015). Each private per-id route answers 404 on a row the caller cannot act on.
*/
const RELEASE_CHANNEL_ROUTES = {
	list: {
		method: "GET",
		path: "/v1/channels"
	},
	listPrivate: {
		method: "GET",
		path: "/v1/channels/private"
	},
	getPrivate: {
		method: "GET",
		path: "/v1/channels/private/:id"
	},
	createPrivate: {
		method: "POST",
		path: "/v1/channels/private"
	},
	update: {
		method: "PATCH",
		path: "/v1/channels/:id"
	},
	updatePrivate: {
		method: "PATCH",
		path: "/v1/channels/private/:id"
	},
	deletePrivate: {
		method: "DELETE",
		path: "/v1/channels/private/:id"
	},
	listShares: {
		method: "GET",
		path: "/v1/channels/shares"
	},
	createShares: {
		method: "POST",
		path: "/v1/channels/shares"
	},
	revokeShare: {
		method: "DELETE",
		path: "/v1/channels/shares/:id"
	},
	shareTargets: {
		method: "GET",
		path: "/v1/channels/share-targets"
	}
};
//#endregion
export { CHANNEL_DESCRIPTION_MAX_LENGTH, CHANNEL_LIST_CAP, CHANNEL_SHARES_PER_ORG_MAX, CHANNEL_SHARE_NOTE_MAX_LENGTH, CHANNEL_SHARE_ORG_IDS_MAX, CHANNEL_SHARE_PATTERN, CHANNEL_SLUG_PATTERN, PRIVATE_CHANNELS_PER_NAMESPACE_MAX, PRIVATE_CHANNEL_FLOOR_CLASS, PRIVATE_CHANNEL_SLUG_MAX_LENGTH, PRIVATE_CHANNEL_SLUG_PATTERN, RELEASE_CHANNEL_ROUTES, channelFloorRing, channelKindSchema, channelShareDecisionStateSchema, channelSharePatternInputSchema, channelShareSchema, channelSlugInputSchema, createChannelShareRequestSchema, createPrivateChannelRequestSchema, matchesChannelSharePattern, normalizeChannelSlugInput, ownerHandleFromEmail, privateChannelKeyFor, privateChannelSlugInputSchema, releaseChannelSchema, shareTargetSchema, updateReleaseChannelRequestSchema };

//# sourceMappingURL=release-channels.js.map